import ModelEntity from "../model-entity"
import UniqueCustomFieldsProxy from "../unique-custom-fields-proxy"
import {EntityType} from "../../gd/const/item"
import {CompanyRole, CompanyRoleLabel} from "../../gd/const/company-role"
import CompanyUserActions from "./actions/actions"
import DatetimeUtils from "../../gd/utils/datetime-utils"
import ApiUser from "../../gd/api/user";
import WorkWeekSchedule from "../work-week-schedule/work-week-schedule"
import User from "../user/user";
import {BinaryWeek} from "../../gd/types/types";
import Department from "../department/department";

const debug = false
// const debug = 'CompanyUser'

/*
    workdayDuration: 'number',  // default work day in minutes // 0 treated as flexible - I THINK NOT IN USE ANYMORE
 */

export default class CompanyUser extends ModelEntity {

    public clockInOutEnabled            : boolean
    public defaultBreakDuration         : number
    public autoClockOutTime             : Moment | null // Local time (!)
    public companyRole                  : CompanyRole
    public departmentId                 : ID
    public reportToUserId?              : ID
    public isAdmin                      : boolean
    public isActivated                  : boolean
    public isDeleted                    : boolean
    public isDemo                       : boolean

    public userId                       : ID
    public user                         : User // any

    public companyId                    : ID
    public company                      : any

    public workWeek                     : BinaryWeek

    public workdayDurations             : number[]

    private _workWeekSchedule?          : WorkWeekSchedule

    public maxPastDueHours              : number
    public requiredTimeReportMinutes    : number
    public email                        : string
    public guesstimate                  : boolean
    public dailyBadge                   : boolean

    public financeInternalCost          : number
    public financeExternalCost          : number

    public timezone                     : string
    public momentCreated                : Moment

    public skills                       : string[]

    // public customFieldsData             : any

    constructor(data:any, useProxy: boolean = true) {
        debug && console.log(debug,"constructor", data);

        const id = data['id'] || '{0}-{1}'.f(data['companyId'],data['userId'])

        super(id, EntityType.COMPANY_USER)

        this.clockInOutEnabled          = data['clockInOutEnabled']
        this.defaultBreakDuration       = data['defaultBreakDuration']
        this.companyRole                = data['companyRole']
        this.departmentId               = data['departmentId']
        this.reportToUserId             = data['reportToUserId']
        this.isAdmin                    = _.get(data, 'isAdmin', false)
        this.isActivated                = _.get(data, 'isActivated', true)
        this.isDeleted                  = _.get(data, 'isDeleted', false)
        this.isDemo                     = _.get(data, 'isDemo', false)

        // init autoClockOutTime
        this.autoClockOutTime    = null
        this.setAutoClockOutTimeFromUTC(data['autoClockOutTime'])
        //

        this.userId                     = data['userId']
        this.user                       = gd.session.users.get(this.userId)!

        this.companyId                  = data['companyId']
        this.company                    = gd.session.companies.get(this.companyId)
        this.workWeek                   = _.get(data,'workWeek', this.company.settings.workSchedule.workWeekSchedule.toArray())

        this.workdayDurations           = _.get(data,'workdayDurations', false)
        if (!this.workdayDurations) this.workdayDurations = this._getDefaultWorkdayDurations(data.workWeek)

        this.maxPastDueHours            = data['maxPastDueHours']
        this.requiredTimeReportMinutes  = data['requiredTimeReportMinutes']
        this.email                      = data['email']
        this.guesstimate                = _.get(data, 'guesstimate', false)
        this.dailyBadge                 = _.get(data, 'dailyBadge', false)

        this.financeInternalCost        = data['financeInternalCost'] || gd.company?.financeDefaultInternalCost
        this.financeExternalCost        = data['financeExternalCost'] || gd.company?.financeDefaultExternalCost

        this.timezone                   = data['timezone']
        this.momentCreated              = data['momentCreated'] ? DatetimeUtils.dateToMoment(data['momentCreated']) : moment()

        this.skills                     = data['skills']

        this.customFieldsData           = data['customFieldsData']

        return useProxy ? new Proxy(this, UniqueCustomFieldsProxy) : this
    }

    setAutoClockOutTimeFromUTC(autoClockOutTime: string) {

        debug && console.log(debug, "setAutoClockOutTimeFromUTC", autoClockOutTime)

        this.autoClockOutTime    = null
        // autoClockOutTime = '18:00:00'
        if (autoClockOutTime) {
            const autoClockOutTimeMomentUtc = moment().utc();
            const [hours, minutes, seconds] = autoClockOutTime.split(':').map(Number)

            autoClockOutTimeMomentUtc.hours(hours)
            autoClockOutTimeMomentUtc.minutes(minutes)
            autoClockOutTimeMomentUtc.seconds(seconds)

            debug && console.log(debug, "autoClockOutTimeMomentUtc", autoClockOutTimeMomentUtc)

            this.autoClockOutTime = autoClockOutTimeMomentUtc.local()


            debug && console.log(debug, "autoClockOutTime=", this.autoClockOutTime.format("HH:mm:ss"), "isUTC=", this.autoClockOutTime?.isUTC())


        }
    }

    get department():Department | null {
        return gd.session.departments.get(this.departmentId)
    }

    get isCustomer():boolean {
        return this.companyRole === CompanyRole.CLIENT
    }

    get name() {
        return this.user.name
    }

    get internal() {
        return this.financeInternalCost
    }

    get external() {
        return this.financeExternalCost
    }

    get workWeekSchedule(): WorkWeekSchedule {
        if (!this._workWeekSchedule) {
            this._workWeekSchedule = new WorkWeekSchedule(
                EntityType.COMPANY_USER,
                this.workdayDurations[0],
                this.workdayDurations[1],
                this.workdayDurations[2],
                this.workdayDurations[3],
                this.workdayDurations[4],
                this.workdayDurations[5],
                this.workdayDurations[6],
            )
        }

        return this._workWeekSchedule
    }

    _getDefaultWorkdayDurations(userWorkweek: any[]) {
        const workdayDurations: number[] = []

        _.map(userWorkweek, workdayDuration=>{
            workdayDurations.push(workdayDuration)
        })

        return workdayDurations
    }

    // used when need to set new value i.e. on user change
    updateWorkdayDurations(newWorkdayDurations: number[]) {
        this.workdayDurations   = newWorkdayDurations ? newWorkdayDurations : this._getDefaultWorkdayDurations(this.workWeek)
    }

    get actions() {
        return new CompanyUserActions(this)
    }

    applyDelta(data:any):boolean {

        const setWorkdayDuration = (index: number, value: number) => {
            this.workdayDurations[index] = value
            delete this._workWeekSchedule
        }

        _.forOwn(data, (value: any ,key)=>{
            if (key === 'id') return

            switch (key) {
                case 'workWeekMon': setWorkdayDuration(0, value); break
                case 'workWeekTue': setWorkdayDuration(1, value); break
                case 'workWeekWed': setWorkdayDuration(2, value); break
                case 'workWeekThu': setWorkdayDuration(3, value); break
                case 'workWeekFri': setWorkdayDuration(4, value); break
                case 'workWeekSat': setWorkdayDuration(5, value); break
                case 'workWeekSun': setWorkdayDuration(6, value); break

                default:
                    // @ts-ignore
                    this[key] =  value
            }
        })

        return true
    }
    async save() {
        const params: any = {}

        debug && console.log(debug,"save", this.updatedFields);

        // this is for universal update
        this.updatedFields.forEach((fieldName) => {
            switch (fieldName) {
                case 'reportToUserId'   :
                    params.reportsTo = this.reportToUserId
                    break

                default                 :
                    // @ts-ignore
                    params[fieldName] = this[fieldName]
            }
        })

        this.updatedFields.clear()

        // console.log("CompanyUser save", this.id, params);
        if (params && _.size(params)>0) return await ApiUser.update(this.id, params);

        return true
    }

    toJSON() {
        let res : JSONObject = {
            id                          : this.id,
            userId                      : this.userId,
            companyId                   : this.companyId,
            email                       : this.email,
            companyRole                 : this.companyRole,
            departmentId                : this.departmentId,
            reportToUserId              : this.reportToUserId,
            isAdmin                     : this.isAdmin,
            isActivated                 : this.isActivated,
            isDeleted                   : this.isDeleted,
            isDemo                      : this.isDemo,
            clockInOutEnabled           : this.clockInOutEnabled,
            defaultBreakDuration        : this.defaultBreakDuration,
            autoClockOutTime            : DatetimeUtils.momentToUTCISODatetime(this.autoClockOutTime),
            workWeek                    : this.workWeek,
            workdayDurations            : this.workdayDurations,
            maxPastDueHours             : this.maxPastDueHours,
            requiredTimeReportMinutes   : this.requiredTimeReportMinutes,
            guesstimate                 : this.guesstimate,
            dailyBadge                  : this.dailyBadge,
            financeInternalCost         : this.financeInternalCost,
            financeExternalCost         : this.financeExternalCost,
            timezone                    : this.timezone,
            momentCreated               : DatetimeUtils.momentToDate(this.momentCreated),
            skills                      : this.skills,
            customFieldsData            : this.customFieldsData,
        }

        /*_.mapKeys(this, (value:any, key)=>{
            switch (key) {
                default:
                    if (key.substring(0,1) === '_') key = key.substring(1)
                    res[key] = value && value.toJSON ? value.toJSON() : value
            }

        })*/

        return res
    }


    // gd.session.companyUsers

    // not sure if in use
    get initials():string {
        return gd.utils.initials(this.name,2)
    }

    get url():string {
        return "u/"+this.id
    }

    get companyRoleName():string {
        return CompanyRoleLabel[this.companyRole]
    }
}
